import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Posts from '../../components/Posts/Posts';
import HeroOverlap from '../../components/HeroOverlap/HeroOverlap';
import ButtonLink from '../../components/Shared/ButtonLink';
import List from '../../components/Shared/List';
import Error from '../../components/Error/Error';
import ReactMarkdown from 'react-markdown';
import { getPageContent } from '../../services/dpc';
import useGTM from '../../hooks/useGTM';
import BusinessCardsTeamSection from '../../components/Contentful/BusinessCardsTeamSection/BusinessCardsTeamSection';
import { getHexValuesInString } from '../../util/colors';
import createGTMClass from '../../util/createGTMClass';
import CTACardv2 from '../../components/CTACardv2/CTACardv2';
import Content from '../../components/Content/Content';
import Styles from './CommercialLending.styled';
import theme from '../../styles/theme';
import useIsMobile from '../../hooks/useIsMobile';

function CommercialLending({
  data: {
    heroCta,
    heroMedia,
    mobileHeroMedia,
    metaDescription,
    mainContent,
    tipsAndToolsPod,
    titleTag,
  } = {},
  error,
  isLoading,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }

  const [triggerGTMEvent] = useGTM();

  const businessCardsSection = useMemo(() => {
    return mainContent?.find(f => f?.businessCards?.length) || null;
  }, [mainContent]);

  const posts =
    tipsAndToolsPod &&
    tipsAndToolsPod.map(
      ({ title, contentTag, category, slug, featuredMedia }) => ({
        title,
        category,
        contentTag,
        thumbnail: `${featuredMedia?.media?.fields?.file?.url}?w=300`,
        slug,
      })
    );

  const { isMobile } = useIsMobile(parseInt(theme.breakpoints.desktop));

  const communityFinanceList = [
    'Park-Owned Home Income Considered',
    'Up to 70% LTV',
    '30-year Amortization',
    'Streamlined Process for Faster Funding',
    '$1,500,000 Minimum Loan Amount',
  ];

  const homeOnlyFinanceList = [
    'Financing for homes purchased from Clayton retail home centers',
    'Competitive terms to help you expand and upgrade your community',
    'Streamlined Process for Faster Funding',
  ];

  return (
    <Layout isLoading={isLoading}>
      <SEO title={titleTag} description={metaDescription} />

      <Styles>
        <HeroOverlap
          position='left'
          background={heroMedia?.[0]?.media?.fields?.file?.url}
          mobileBackground={mobileHeroMedia?.[0]?.media?.fields?.file?.url}
          alt={heroMedia?.[0]?.altText}
        >
          <h1>{heroCta?.ctaHeader}</h1>
          <ReactMarkdown
            source={heroCta?.ctaIntro}
            renderers={{
              link: ({ href, children }) => (
                <a
                  className={createGTMClass('phone', 'hero', 'text')}
                  href={href}
                >
                  {children}
                </a>
              ),
            }}
          />
          <ButtonLink
            appearance='dark'
            to={heroCta?.firstCTAButtonUrl}
            size='large'
            className={createGTMClass('contact', 'hero', 'button')}
            onClick={triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Commercial Lending',
              category: 'loan_evaluation',
              action: 'button',
              label: 'commercial_quote',
            })}
          >
            {heroCta?.firstCTAButtonText}
          </ButtonLink>
        </HeroOverlap>

        <Content
          className='main-content'
          divider={false}
          width={theme.maxWidths.container}
        >
          <section className='header-section'>
            <h2>We Finance Communities</h2>
            <p>
              Tailored Lending Solutions for Mobile Home Parks & Manufactured
              Housing.
            </p>
          </section>
          <section className='finance-section'>
            <List title='Flexible Terms:' items={communityFinanceList} />
          </section>
          <section className='home-only-section'>
            <h2>Home-Only Finance Programs</h2>
            <List title='For Community Owners' items={homeOnlyFinanceList} />
          </section>
          <section className='highlights-section'>
            <h2 className='color-grey1'>
              Expertise You Can Trust | Fast & Efficient Process | Customized
              Financing
            </h2>
          </section>
        </Content>

        {businessCardsSection && (
          <BusinessCardsTeamSection
            businessCards={businessCardsSection?.businessCards}
            backgroundColor={
              getHexValuesInString(
                businessCardsSection?.sectionBackgroundColor
              )[0]
            }
            header={businessCardsSection?.sectionHeader}
            headerTextColor={
              getHexValuesInString(
                businessCardsSection?.sectionHeaderTextColor
              )[0]
            }
          />
        )}
        <Posts
          title='Tips & Tools'
          link={{ title: 'View all', to: '/resources/blog/' }}
          posts={posts}
        />

        <CTACardv2
          preHeaderContent={'Have Questions?'}
          headerContent={`We're Here to Help`}
          ctaIntro={
            <>
              <p>
                Contact Kurt Wilkerson & Seth Ray <br />
                <a
                  href='mailto:clt@vmf.com'
                  className={createGTMClass('email', 'prefoot_cta', 'text')}
                >
                  CLT@vmf.com
                </a>
              </p>
            </>
          }
          buttonText={isMobile ? 'Contact Us' : null}
          buttonUrl={isMobile ? 'mailto:clt@vmf.com' : null}
          buttonLinkExternal={true}
        />
      </Styles>
    </Layout>
  );
}

CommercialLending.getInitialProps = async ({}) => {
  try {
    const data = await getPageContent('/commercial-lending/');

    if (data.status !== 200) {
      throw data.statusText;
    }

    return {
      data: data.data.result,
    };
  } catch (error) {
    return {
      error,
    };
  }
};

CommercialLending.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default CommercialLending;
